import { get, patch, post } from '@/api/request'
import type { ApiResponse } from '@/api/request'
import type { ReferralInfo, ReferralLevel, Transaction, User } from '@/types/api'

export interface WalletConnection {
  id: string
  walletAddress: string
  chainId: number
  chainName: string
  connectedAt: string
  lastActiveAt: string
  isActive: boolean
}

export interface ReferralStatsPayload {
  level1Count: number
  level2Count: number
  level3Count: number
  totalCommission: number
  level1Commission?: number
  level2Commission?: number
  level3Commission?: number
}

export interface ReferralsResponse {
  referralCode: string
  stats: ReferralStatsPayload
  levels: ReferralLevel[]
}

export interface ReferralListResponse {
  level: number
  referrals: ReferralInfo[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface TransactionsResponse {
  transactions: Transaction[]
  pagination?: { total: number }
}

export const usersApi = {
  async getProfile(): Promise<
    ApiResponse<{ id: string; email?: string; nickname?: string; avatar?: string }>
  > {
    const res = await get<{ id: string; email?: string; nickname?: string; avatar?: string }>(
      '/users/profile',
    )
    return res.data
  },

  async updateProfile(data: { nickname?: string; avatar?: string }): Promise<ApiResponse<User>> {
    const res = await patch<User>('/users/profile', data)
    return res.data
  },

  async getBalance(): Promise<
    ApiResponse<{ balance: { BTC: number; BLD: number }; totalBalanceUSD: number }>
  > {
    const res = await get<{ balance: { BTC: number; BLD: number }; totalBalanceUSD: number }>(
      '/users/balance',
    )
    return res.data
  },

  async getTransactions(params?: {
    page?: number
    limit?: number
    type?: string
  }): Promise<ApiResponse<TransactionsResponse>> {
    const res = await get<TransactionsResponse>('/users/transactions', { params })
    return res.data
  },

  async getReferralStats(): Promise<ApiResponse<ReferralsResponse>> {
    const res = await get<ReferralsResponse>('/users/referrals')
    return res.data
  },

  async getReferralList(params: {
    level: number
    page?: number
    limit?: number
  }): Promise<ApiResponse<ReferralListResponse>> {
    const res = await get<ReferralListResponse>('/users/referrals/list', { params })
    return res.data
  },

  async reportClientInfo(os: string, pwaInstalled: boolean): Promise<ApiResponse<void>> {
    const res = await post<void>('/users/client-info', { os, pwaInstalled })
    return res.data
  },

  async getWalletConnections(): Promise<ApiResponse<{ connections: WalletConnection[] }>> {
    const res = await get<{ connections: WalletConnection[] }>('/users/wallet/list')
    return res.data
  },
}
